import React, { useEffect, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useRulesStore, type Rule, type RuleField, type RuleOperator } from '@/store/rulesStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { usePayeesStore } from '@/store/payeesStore';
import { useTagsStore } from '@/store/tagsStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, Edit2, X, Settings2, ArrowRight, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Combobox } from '@/components/ui/combobox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion } from 'framer-motion';
import { CategorySelect } from '@/components/transactions/CategorySelect';

const fields: { value: RuleField; label: string }[] = [
  { value: 'description', label: 'Description' },
  { value: 'category', label: 'Category' },
  { value: 'payee', label: 'Payee' },
  { value: 'notes', label: 'Notes' },
  { value: 'tags', label: 'Tags' },
];

const operators: { value: RuleOperator; label: string }[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
];

const defaultRule: Omit<Rule, 'id'> = {
  name: '',
  active: true,
  user_id: '',
  condition: {
    field: 'description' as RuleField,
    operator: 'contains' as RuleOperator,
    value: '',
  },
  actions: [{
    field: 'category' as RuleField,
    value: '',
  }],
  created_at: '',
  updated_at: '',
};

type DialogMode = 'add' | 'edit';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const RuleForm = ({ 
  editingRule, 
  setEditingRule,
  renderValueInput 
}: { 
  editingRule: Omit<Rule, 'id'>, 
  setEditingRule: any,
  renderValueInput: (field: RuleField, isAction: boolean, index: number) => React.ReactNode
}) => {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Rule Name</Label>
        <Input
          id="name"
          value={editingRule.name}
          onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })}
          placeholder="Enter rule name"
          className="h-11 w-full rounded-xl border border-border bg-background-input dark:bg-background-dark-input dark:border-border-dark text-text-primary dark:text-text-dark-primary shadow-sm focus:ring-2 focus:ring-primary dark:focus:ring-primary-dark"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Condition</Label>
          <div className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary dark:bg-primary-dark/10 dark:text-primary-dark">
            If
          </div>
        </div>
        <div className="space-y-3 p-4 rounded-xl bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark">
          <div className="flex flex-col sm:flex-row gap-3">
            <Select
              value={editingRule.condition.field}
              onValueChange={(value: RuleField) =>
                setEditingRule({
                  ...editingRule,
                  condition: { ...editingRule.condition, field: value, value: '' }
                })
              }
            >
              <SelectTrigger className="w-full sm:w-[180px] h-11 rounded-xl border border-border bg-background-secondary dark:bg-background-dark-secondary dark:border-border-dark text-text-primary dark:text-text-dark-primary">
                <SelectValue placeholder="Select field" />
              </SelectTrigger>
              <SelectContent className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark rounded-lg">
                {fields.map((field) => (
                  <SelectItem key={field.value} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={editingRule.condition.operator}
              onValueChange={(value: RuleOperator) =>
                setEditingRule({
                  ...editingRule,
                  condition: { ...editingRule.condition, operator: value }
                })
              }
            >
              <SelectTrigger className="w-full sm:w-[180px] h-11 rounded-xl border border-border bg-background-secondary dark:bg-background-dark-secondary dark:border-border-dark text-text-primary dark:text-text-dark-primary">
                <SelectValue placeholder="Select operator" />
              </SelectTrigger>
              <SelectContent className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark rounded-lg">
                {operators.map((operator) => (
                  <SelectItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full">
            {renderValueInput(editingRule.condition.field, false, 0)}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Actions</Label>
          <div className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary dark:bg-primary-dark/10 dark:text-primary-dark">
            Then
          </div>
        </div>
        <div className="space-y-3">
          {editingRule.actions.map((action, index) => (
            <div key={index} className="flex flex-col sm:flex-row gap-3 p-4 rounded-xl bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark">
              <Select
                value={action.field}
                onValueChange={(value: RuleField) =>
                  setEditingRule({
                    ...editingRule,
                    actions: editingRule.actions.map((a, i) =>
                      i === index ? { ...a, field: value, value: '' } : a
                    ),
                  })
                }
              >
                <SelectTrigger className="w-full sm:w-[180px] h-11 rounded-xl border border-border bg-background-secondary dark:bg-background-dark-secondary dark:border-border-dark text-text-primary dark:text-text-dark-primary">
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark rounded-lg">
                  {fields.map((field) => (
                    <SelectItem key={field.value} value={field.value}>
                      {field.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex-1">
                {renderValueInput(action.field, true, index)}
              </div>

              {editingRule.actions.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-destructive hover:text-destructive/90 hover:bg-error/10 h-11 w-11 rounded-xl"
                  onClick={() => {
                    setEditingRule({
                      ...editingRule,
                      actions: editingRule.actions.filter((_, i) => i !== index),
                    });
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditingRule({
                ...editingRule,
                actions: [
                  ...editingRule.actions,
                  { field: 'category' as RuleField, value: '' },
                ],
              });
            }}
            className="w-full h-11 rounded-xl border border-dashed border-border dark:border-border-dark hover:border-secondary dark:hover:border-primary-dark hover:text-secondary dark:hover:text-primary-dark transition-colors"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Action
          </Button>
        </div>
      </div>

      <div className="flex items-center space-x-3 p-4 rounded-xl bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark">
        <Switch
          id="active"
          checked={editingRule.active}
          onCheckedChange={(checked) =>
            setEditingRule({ ...editingRule, active: checked })
          }
          className="data-[state=checked]:bg-primary dark:data-[state=checked]:bg-primary-dark"
        />
        <div>
          <Label htmlFor="active" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Active</Label>
          <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
            {editingRule.active ? 'This rule will be applied to new transactions' : 'This rule is currently disabled'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function RulesSettings() {
  const { user } = useAuthStore();
  const { rules, fetchRules, addRule, updateRule, deleteRule } = useRulesStore();
  const { categories, fetchCategories } = useCategoriesStore();
  const { payees, fetchPayees } = usePayeesStore();
  const { tags, fetchTags } = useTagsStore();
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>('add');
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<Omit<Rule, 'id'>>(defaultRule);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRules = rules.filter(rule => {
    const searchLower = searchQuery.toLowerCase();
    return (
      rule.name.toLowerCase().includes(searchLower) ||
      rule.condition.field.toLowerCase().includes(searchLower) ||
      rule.condition.value.toLowerCase().includes(searchLower) ||
      rule.actions.some(action => action.field.toLowerCase().includes(searchLower) || action.value.toLowerCase().includes(searchLower))
    );
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      
      try {
        await Promise.all([
          fetchRules(),
          fetchCategories(),
          fetchPayees(user.id),
          fetchTags(user.id)
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [user?.id]);

  const handleOpenDialog = (mode: DialogMode, rule?: Rule) => {
    setDialogMode(mode);
    if (mode === 'edit' && rule) {
      setSelectedRuleId(rule.id);
      setEditingRule({
        name: rule.name,
        active: rule.active,
        user_id: rule.user_id,
        condition: { ...rule.condition },
        actions: Array.isArray(rule.actions) ? [...rule.actions] : [{ field: 'category', value: '' }],
        created_at: rule.created_at,
        updated_at: rule.updated_at,
      });
    } else {
      setSelectedRuleId(null);
      setEditingRule(defaultRule);
    }
    setShowDialog(true);
  };

  const handleSaveRule = async () => {
    console.log('handleSaveRule called with:', { user, editingRule, dialogMode, selectedRuleId });
    
    if (!user?.id) {
      console.error('No user ID available');
      return;
    }
    
    if (!editingRule.name) {
      console.error('Rule name is required');
      return;
    }
    
    if (!editingRule.condition.value) {
      console.error('Condition value is required');
      return;
    }
    
    if (editingRule.actions.some(action => !action.value)) {
      console.error('All actions must have values');
      return;
    }
    
    try {
      console.log('About to save rule with data:', editingRule);
      
      if (dialogMode === 'add') {
        console.log('Adding new rule...');
        const result = await addRule({
          ...editingRule,
          user_id: user.id
        });
        console.log('Rule added successfully:', result);
      } else if (dialogMode === 'edit' && selectedRuleId) {
        console.log('Updating existing rule...');
        await updateRule(selectedRuleId, editingRule);
        console.log('Rule updated successfully');
      }
      
      setShowDialog(false);
      setEditingRule(defaultRule);
      setSelectedRuleId(null);
    } catch (error) {
      console.error('Error saving rule:', error);
    }
  };

  const handleToggleRule = async (rule: Rule) => {
    if (!user?.id) return;
    try {
      await updateRule(rule.id, { active: !rule.active });
    } catch (error) {
      console.error('Error toggling rule:', error);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!user?.id) return;
    try {
      await deleteRule(ruleId);
    } catch (error) {
      console.error('Error deleting rule:', error);
    }
  };

  const getFieldOptions = (field: RuleField) => {
    switch (field) {
      case 'category':
        return categories.flatMap(mainCategory => [
          {
            label: `${mainCategory.icon} ${mainCategory.name}`,
            value: mainCategory.id,
            color: mainCategory.color,
            isMainCategory: true
          },
          ...mainCategory.subcategories.map((sub: { id: string; name: string; color: string }) => ({
            label: `↳ ${sub.name}`,
            value: sub.id,
            color: sub.color,
            parentCategory: mainCategory.name
          }))
        ]);
      case 'payee':
        return payees.map(payee => ({
          label: payee.name,
          value: payee.id,
        }));
      case 'tags':
        return tags.map(tag => ({
          label: tag.name,
          value: tag.id,
          color: tag.color
        }));
      default:
        return [];
    }
  };

  const getDisplayValue = (field: RuleField, value: string) => {
    switch (field) {
      case 'category':
        const category = categories.find(cat => 
          cat.id === value || cat.subcategories.some((sub: { id: string }) => sub.id === value)
        );
        if (category) {
          const subcategory = category.subcategories.find((sub: { id: string; name: string }) => sub.id === value);
          return subcategory ? subcategory.name : category.name;
        }
        return value;
      case 'payee':
        const payee = payees.find(p => p.id === value);
        return payee ? payee.name : value;
      case 'tags':
        const tag = tags.find(t => t.id === value);
        return tag ? tag.name : value;
      default:
        return value;
    }
  };

  const renderValueInput = (field: RuleField, isAction: boolean, index: number) => {
    const options = getFieldOptions(field);
    
    if (field === 'category') {
      return (
        <CategorySelect
          value={{
            categoryId: isAction ? editingRule.actions[index].value : editingRule.condition.value,
            category: getDisplayValue(field, isAction ? editingRule.actions[index].value : editingRule.condition.value)
          }}
          onChange={(categoryId) => {
            if (isAction) {
              setEditingRule({
                ...editingRule,
                actions: editingRule.actions.map((a, i) =>
                  i === index ? { ...a, value: categoryId } : a
                ),
              });
            } else {
              setEditingRule({
                ...editingRule,
                condition: { ...editingRule.condition, value: categoryId }
              });
            }
          }}
        />
      );
    }
    
    if (options.length > 0) {
      return (
        <Combobox
          items={options}
          value={isAction ? editingRule.actions[index].value : editingRule.condition.value}
          onChange={(value) => {
            if (isAction) {
              setEditingRule({
                ...editingRule,
                actions: editingRule.actions.map((a, i) =>
                  i === index ? { ...a, value } : a
                ),
              });
            } else {
              setEditingRule({
                ...editingRule,
                condition: { ...editingRule.condition, value }
              });
            }
          }}
          placeholder={
            isAction 
              ? `Select ${fields.find(f => f.value === field)?.label || field}`
              : `Enter value for ${fields.find(f => f.value === field)?.label || field}`
          }
          className="bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark"
        />
      );
    }

    return (
      <Input
        placeholder="Value"
        value={isAction ? editingRule.actions[index].value : editingRule.condition.value}
        onChange={(e) => {
          if (isAction) {
            setEditingRule({
              ...editingRule,
              actions: editingRule.actions.map((a, i) =>
                i === index ? { ...a, value: e.target.value } : a
              ),
            });
          } else {
            setEditingRule({
              ...editingRule,
              condition: { ...editingRule.condition, value: e.target.value }
            });
          }
        }}
        className="h-11 w-full rounded-xl border border-border bg-background-input dark:bg-background-dark-input dark:border-border-dark text-text-primary dark:text-text-dark-primary shadow-sm focus:ring-2 focus:ring-primary dark:focus:ring-primary-dark"
      />
    );
  };

  // Empty state when no rules exist
  const EmptyState = () => (
    <div className="text-center py-12">
      <div className="mx-auto rounded-xl bg-background-secondary/20 dark:bg-background-dark-secondary/20 w-16 h-16 flex items-center justify-center mb-4">
        <Settings2 className="w-8 h-8 text-secondary dark:text-primary-dark" />
      </div>
      <h3 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
        No automation rules yet
      </h3>
      <p className="text-text-secondary dark:text-text-dark-secondary mb-6 max-w-sm mx-auto">
        Automation rules help you categorize transactions automatically based on your criteria.
      </p>
      <Button 
        onClick={() => handleOpenDialog('add')}
        className="bg-primary text-white hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 shadow-sm rounded-xl h-11"
      >
        <Plus className="mr-2 h-4 w-4" /> Create first rule
      </Button>
    </div>
  );

  // No results found when search returns no matches
  const NoResults = () => (
    <div className="text-center py-12">
      <div className="mx-auto rounded-xl bg-background-secondary/20 dark:bg-background-dark-secondary/20 w-16 h-16 flex items-center justify-center mb-4">
        <Search className="w-8 h-8 text-secondary dark:text-primary-dark" />
      </div>
      <h3 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
        No matching rules found
      </h3>
      <p className="text-text-secondary dark:text-text-dark-secondary mb-6 max-w-sm mx-auto">
        Try adjusting your search terms or clear the search to view all rules.
      </p>
      <Button 
        onClick={() => setSearchQuery('')}
        variant="outline"
        className="border-border dark:border-border-dark rounded-xl h-11"
      >
        Clear search
      </Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-sm px-4 sm:px-6 py-4 sm:py-6 sticky top-0 z-10">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10 dark:bg-primary-dark/10">
              <Settings2 className="w-5 h-5 text-primary dark:text-primary-dark" />
            </div>
            <div>
              <h1 className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                Automation Rules
              </h1>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Setup rules to automatically process transactions.
              </p>
            </div>
          </div>
          
          <Button
            onClick={() => handleOpenDialog('add')}
            className="bg-primary text-white hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 shadow-sm rounded-xl h-11"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Rule
          </Button>
        </div>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary dark:text-text-dark-secondary h-4 w-4" />
          <Input
            type="text"
            placeholder="Search rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-11 bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark rounded-xl"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-secondary dark:hover:bg-background-dark-secondary"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {rules.length === 0 ? (
        <EmptyState />
      ) : filteredRules.length === 0 ? (
        <NoResults />
      ) : (
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="space-y-4"
        >
          {filteredRules.map((rule) => (
            <motion.div 
              key={rule.id} 
              variants={item}
              className="group bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
            >
              <div className="px-4 py-4 sm:px-6">
                <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary">{rule.name}</h3>
                      <div className={`px-2 py-0.5 text-xs rounded-full ${rule.active ? 'bg-primary/10 text-primary dark:bg-primary-dark/10 dark:text-primary-dark' : 'bg-text-secondary/10 text-text-secondary dark:text-text-dark-secondary'}`}>
                        {rule.active ? 'Active' : 'Inactive'}
                      </div>
                    </div>
                    
                    <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-2">
                      <div className="flex flex-wrap items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                        <span className="font-medium">If:</span>
                        <span className="px-2 py-0.5 rounded-md bg-background-primary dark:bg-background-dark-primary border border-border dark:border-border-dark">
                          {fields.find(f => f.value === rule.condition.field)?.label || rule.condition.field}
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-background-primary dark:bg-background-dark-primary border border-border dark:border-border-dark">
                          {operators.find(o => o.value === rule.condition.operator)?.label || rule.condition.operator}
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-background-primary dark:bg-background-dark-primary border border-border dark:border-border-dark line-clamp-1 flex-1 max-w-full">
                          {getDisplayValue(rule.condition.field, rule.condition.value)}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                        <span className="font-medium">Then:</span>
                        {rule.actions.map((action, idx) => (
                          <div key={idx} className="flex items-center gap-1 flex-wrap">
                            <span className="px-2 py-0.5 rounded-md bg-background-primary dark:bg-background-dark-primary border border-border dark:border-border-dark whitespace-nowrap">
                              {fields.find(f => f.value === action.field)?.label || action.field}
                            </span>
                            <ArrowRight className="h-3 w-3" />
                            <span className="px-2 py-0.5 rounded-md bg-background-primary dark:bg-background-dark-primary border border-border dark:border-border-dark line-clamp-1 max-w-[200px]">
                              {getDisplayValue(action.field, action.value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 sm:opacity-70 sm:group-hover:opacity-100 transition-opacity">
                    <Switch
                      checked={rule.active}
                      onCheckedChange={() => handleToggleRule(rule)}
                      className="data-[state=checked]:bg-primary dark:data-[state=checked]:bg-primary-dark"
                    />
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog('edit', rule)}
                      className="h-9 w-9 rounded-lg hover:bg-primary/5 dark:hover:bg-primary-dark/10 text-text-primary dark:text-text-dark-primary"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteRule(rule.id)}
                      className="h-9 w-9 rounded-lg hover:bg-error/5 dark:hover:bg-error/10 text-destructive dark:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
      
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
              {dialogMode === 'add' ? 'Add New Rule' : 'Edit Rule'}
            </DialogTitle>
            <DialogDescription className="text-text-secondary dark:text-text-dark-secondary">
              Set up a rule to automatically process transactions based on matching criteria.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[70vh] max-h-[600px] pr-4 -mr-4">
            <RuleForm
              editingRule={editingRule}
              setEditingRule={setEditingRule}
              renderValueInput={renderValueInput}
            />
          </ScrollArea>
          
          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 sm:gap-0 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="w-full sm:w-auto bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-primary dark:text-text-dark-primary border-border dark:border-border-dark rounded-xl h-11"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRule}
              disabled={!editingRule.name || !editingRule.condition.value || editingRule.actions.some(action => !action.value)}
              className="w-full sm:w-auto bg-primary text-white hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 shadow-sm rounded-xl h-11"
            >
              {dialogMode === 'add' ? 'Add Rule' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
